/** Input module types: vectors, key/mouse enums, state interfaces, config. */

export interface Vector2Like {
    x: number;
    y: number;
}

export const MouseButton = {
    LEFT: 0,
    MIDDLE: 1,
    RIGHT: 2,
} as const;

export type MouseButton = typeof MouseButton[keyof typeof MouseButton];

/** Common key codes. Use isKeyDown("KeyQ") or KeyCode.W. */
export const KeyCode = {
    // Movement keys
    W: "KeyW",
    A: "KeyA",
    S: "KeyS",
    D: "KeyD",
    SPACE: "Space",

    // Arrow keys
    ARROW_UP: "ArrowUp",
    ARROW_DOWN: "ArrowDown",
    ARROW_LEFT: "ArrowLeft",
    ARROW_RIGHT: "ArrowRight",

    // Common game keys
    ENTER: "Enter",
    ESCAPE: "Escape",
    SHIFT_LEFT: "ShiftLeft",
    SHIFT_RIGHT: "ShiftRight",
    CTRL_LEFT: "ControlLeft",
    CTRL_RIGHT: "ControlRight",
    ALT_LEFT: "AltLeft",
    ALT_RIGHT: "AltRight",

    // Letters (A-Z)
    Q: "KeyQ",
    E: "KeyE",
    R: "KeyR",
    T: "KeyT",
    Y: "KeyY",
    U: "KeyU",
    I: "KeyI",
    O: "KeyO",
    P: "KeyP",
    F: "KeyF",
    G: "KeyG",
    H: "KeyH",
    J: "KeyJ",
    K: "KeyK",
    L: "KeyL",
    Z: "KeyZ",
    X: "KeyX",
    C: "KeyC",
    V: "KeyV",
    B: "KeyB",
    N: "KeyN",
    M: "KeyM",

    // Numbers (0-9)
    DIGIT_0: "Digit0",
    DIGIT_1: "Digit1",
    DIGIT_2: "Digit2",
    DIGIT_3: "Digit3",
    DIGIT_4: "Digit4",
    DIGIT_5: "Digit5",
    DIGIT_6: "Digit6",
    DIGIT_7: "Digit7",
    DIGIT_8: "Digit8",
    DIGIT_9: "Digit9",

    // Function keys
    F1: "F1",
    F2: "F2",
    F3: "F3",
    F4: "F4",
    F5: "F5",
    F6: "F6",
    F7: "F7",
    F8: "F8",
    F9: "F9",
    F10: "F10",
    F11: "F11",
    F12: "F12",

    // Special keys
    TAB: "Tab",
    CAPS_LOCK: "CapsLock",
    BACKSPACE: "Backspace",
    DELETE: "Delete",
    INSERT: "Insert",
    HOME: "Home",
    END: "End",
    PAGE_UP: "PageUp",
    PAGE_DOWN: "PageDown",
    PRINT_SCREEN: "PrintScreen",
    SCROLL_LOCK: "ScrollLock",
    PAUSE: "Pause",
    NUM_LOCK: "NumLock",

    // Numpad
    NUMPAD_0: "Numpad0",
    NUMPAD_1: "Numpad1",
    NUMPAD_2: "Numpad2",
    NUMPAD_3: "Numpad3",
    NUMPAD_4: "Numpad4",
    NUMPAD_5: "Numpad5",
    NUMPAD_6: "Numpad6",
    NUMPAD_7: "Numpad7",
    NUMPAD_8: "Numpad8",
    NUMPAD_9: "Numpad9",
    NUMPAD_ADD: "NumpadAdd",
    NUMPAD_SUBTRACT: "NumpadSubtract",
    NUMPAD_MULTIPLY: "NumpadMultiply",
    NUMPAD_DIVIDE: "NumpadDivide",
    NUMPAD_ENTER: "NumpadEnter",
    NUMPAD_DECIMAL: "NumpadDecimal",

    // Punctuation and symbols
    SEMICOLON: "Semicolon",
    EQUAL: "Equal",
    COMMA: "Comma",
    MINUS: "Minus",
    PERIOD: "Period",
    SLASH: "Slash",
    BACKQUOTE: "Backquote",
    BRACKET_LEFT: "BracketLeft",
    BACKSLASH: "Backslash",
    BRACKET_RIGHT: "BracketRight",
    QUOTE: "Quote",
} as const;

export type KeyCode = typeof KeyCode[keyof typeof KeyCode];

export interface MousePosition {
    screen: Vector2Like; // Screen coordinates (pixels from top-left)
    canvas: Vector2Like; // Canvas coordinates (pixels from canvas top-left)
    normalized: Vector2Like; // Normalized coordinates (-1 to 1, center origin)
}

export interface TouchPoint {
    id: number;
    position: MousePosition;
    force?: number; // Touch pressure (if supported)
}

export interface KeyboardState {
    pressedKeys: Set<string>;
    lastPressedKey: string | null;
}

export interface MouseState {
    position: MousePosition;
    pressedButtons: Set<MouseButton>;
    wheelDelta: Vector2Like;
    isOverCanvas: boolean;
    delta: Vector2Like; // Mouse movement since last frame
    velocity: Vector2Like; // Mouse velocity (pixels per second)
}

/**
 * Touch state
 */
export interface TouchState {
    activeTouches: Map<number, TouchPoint>;
    touchCount: number;
    isActive: boolean;
}

export type JoystickZonePosition = 
    | "bottom-left" 
    | "bottom-right" 
    | "top-left" 
    | "top-right" 
    | "center"
    | "custom";

export interface JoystickConfig {
    /** Unique identifier for the joystick */
    id: string;
    
    /** Zone position on screen */
    zone: JoystickZonePosition;
    
    /** Custom position (used when zone is "custom") - screen percentage (0-1) */
    customPosition?: { x: number; y: number };
    
    /** Zone radius in pixels (area where touch activates joystick) */
    zoneRadius?: number;
    
    /** Maximum stick distance from center in pixels */
    maxDistance?: number;
    
    /** Deadzone threshold (0-1, normalized) */
    deadzone?: number;
    
    /** Enable visual rendering */
    showVisual?: boolean;
    
    /** Stick returns to center when released (false = stays at last position) */
    autoReturn?: boolean;
}

export interface JoystickState {
    /** Joystick ID */
    id: string;
    
    /** Is joystick currently active (being touched) */
    isActive: boolean;
    
    /** Normalized joystick vector (-1 to 1 on both axes) */
    vector: Vector2Like;
    
    /** Raw vector (in pixels from center) */
    rawVector: Vector2Like;
    
    /** Distance from center (0-1, normalized) */
    distance: number;
    
    /** Angle in radians (-PI to PI) */
    angle: number;
    
    /** Touch ID currently controlling this joystick */
    touchId: number | null;
    
    /** Center position of joystick (updated when touch starts) */
    centerPosition: Vector2Like;
    
    /** Current touch position */
    currentPosition: Vector2Like;
}

export interface InputManagerConfig {
    /** Target element for events (default: document) */
    target?: HTMLElement | Document;

    /** Prevent default event behavior */
    preventDefault?: boolean;

    /** Stop event propagation */
    stopPropagation?: boolean;

    /** Enable touch support */
    enableTouch?: boolean;

    /** Canvas element for coordinate calculations */
    canvas?: HTMLCanvasElement;
    
    /** Virtual joystick configurations */
    joysticks?: JoystickConfig[];
}

export type SwipeDirection =
    | "left"
    | "right"
    | "up"
    | "down"
    | "up-left"
    | "up-right"
    | "down-left"
    | "down-right";

export interface SwipeData {
    direction: SwipeDirection;
    distance: number;
    startPosition: Vector2Like;
    endPosition: Vector2Like;
    duration: number;
}
